import { getPageSeo } from './getPageSeo.js';

/**
 * @param {string} selector
 * @param {Record<string, string>} attrs
 */
function upsertMeta(selector, attrs) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

/**
 * @param {string} selector
 * @param {Record<string, string>} attrs
 */
function upsertLink(selector, attrs) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

/**
 * Replace hreflang alternate links in document head.
 *
 * @param {Array<{ hreflang: string, href: string }>} alternates
 */
function syncHreflangLinks(alternates) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => {
    node.remove();
  });

  alternates.forEach(({ hreflang, href }) => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = hreflang;
    link.href = href;
    link.setAttribute('data-seo-hreflang', 'true');
    document.head.appendChild(link);
  });
}

/**
 * Apply SEO metadata to document (client-side route / language changes).
 *
 * @param {ReturnType<typeof getPageSeo>} seo
 */
export function applyDocumentSeo(seo) {
  if (typeof document === 'undefined') {
    return;
  }

  document.title = seo.title;
  document.documentElement.lang = seo.htmlLang;

  upsertMeta('meta[name="description"]', { name: 'description', content: seo.description });
  upsertMeta('meta[name="robots"]', { name: 'robots', content: seo.robots });
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'AquaMind' });
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title });
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: seo.canonicalUrl });
  upsertMeta('meta[property="og:image"]', { property: 'og:image', content: seo.imageUrl });
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: seo.ogLocale });
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title });
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description });
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: seo.imageUrl });
  upsertLink('link[rel="canonical"]', { rel: 'canonical', href: seo.canonicalUrl });

  syncHreflangLinks(seo.hreflangAlternates);

  let script = document.head.querySelector('script[data-seo="structured-data"]');

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'structured-data');
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(seo.structuredData);
}

/**
 * Resolve site origin for SEO (env in build, location in browser).
 *
 * @returns {string}
 */
export function resolveSiteUrl() {
  if (typeof window !== 'undefined') {
    return (import.meta.env.VITE_SITE_URL || window.location.origin).replace(/\/$/, '');
  }

  return (import.meta.env.VITE_SITE_URL || 'https://aquamind.deep-edge.ai').replace(/\/$/, '');
}
